import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  getCatalogoExamesControllerListarQueryKey,
  getCatalogoExamesControllerObterQueryKey,
  useCatalogoExamesControllerAtualizar,
  useCatalogoExamesControllerCriar,
} from '@/api/generated/admin-catalogo-exames/admin-catalogo-exames';
import type { CatalogoExameResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

const schema = z.object({
  nome: z.string().trim().min(2, 'Informe o nome do exame').max(200),
  categoria: z.string().trim().max(120),
  metodoAnalitico: z.string().trim().max(120, 'No máximo 120 caracteres'),
  codigoLaboratorio: z.string().trim().max(60),
  codigoVertis: z.string().trim().max(60),
  descricao: z.string().trim().max(2000),
});

type Formulario = z.infer<typeof schema>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = criação. */
  exame?: CatalogoExameResponse;
}

export function ExameFormDialogo({ aberto, aoFechar, exame }: Props) {
  const queryClient = useQueryClient();
  const editando = Boolean(exame);

  const criar = useCatalogoExamesControllerCriar();
  const atualizar = useCatalogoExamesControllerAtualizar();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!aberto) return;
    reset({
      nome: exame?.nome ?? '',
      categoria: exame?.categoria ?? '',
      metodoAnalitico: exame?.metodoAnalitico ?? '',
      codigoLaboratorio: exame?.codigoLaboratorio ?? '',
      codigoVertis: exame?.codigoVertis ?? '',
      descricao: exame?.descricao ?? '',
    });
  }, [aberto, exame, reset]);

  const aoEnviar = handleSubmit(async (dados) => {
    const opcional = (v: string) => (v.trim() ? v.trim() : null);

    const corpo = {
      nome: dados.nome,
      categoria: opcional(dados.categoria),
      metodoAnalitico: opcional(dados.metodoAnalitico),
      codigoLaboratorio: opcional(dados.codigoLaboratorio),
      codigoVertis: opcional(dados.codigoVertis),
      descricao: opcional(dados.descricao),
    };

    try {
      if (exame) {
        await atualizar.mutateAsync({ id: exame.id, data: corpo });
        await queryClient.invalidateQueries({
          queryKey: getCatalogoExamesControllerObterQueryKey(exame.id),
        });
      } else {
        await criar.mutateAsync({ data: corpo });
      }

      await queryClient.invalidateQueries({
        queryKey: getCatalogoExamesControllerListarQueryKey(),
      });
      toast.success(editando ? 'Exame atualizado.' : 'Exame criado.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar o exame.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar exame' : 'Novo exame'}</DialogTitle>
          <DialogDescription>
            Os analitos e as faixas de referência são cadastrados na tela do exame.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo id="nome" rotulo="Nome" erro={errors.nome?.message} obrigatorio>
            <Input
              id="nome"
              className={CLASSE_CONTROLE}
              placeholder="Ex.: Hemograma completo"
              {...register('nome')}
            />
          </Campo>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Campo
              id="categoria"
              rotulo="Categoria"
              erro={errors.categoria?.message}
              dica="Agrupa o exame na listagem. Ex.: Hematologia, Bioquímica."
            >
              <Input id="categoria" className={CLASSE_CONTROLE} {...register('categoria')} />
            </Campo>

            {/* Ao lado da categoria porque é da mesma natureza: descreve o
                ensaio, não uma coleta. É o que a Análise Comparativa recorta. */}
            <Campo
              id="metodoAnalitico"
              rotulo="Método analítico"
              erro={errors.metodoAnalitico?.message}
              dica="Cortisol por RIE e por quimioluminescência não têm a mesma escala: comparar coortes medidas por métodos diferentes desloca a média sem estourar nada."
            >
              <Input
                id="metodoAnalitico"
                className={CLASSE_CONTROLE}
                placeholder="Ex.: RIE"
                {...register('metodoAnalitico')}
              />
            </Campo>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Campo
              id="codigoLaboratorio"
              rotulo="Código do laboratório"
              erro={errors.codigoLaboratorio?.message}
            >
              <Input
                id="codigoLaboratorio"
                className={CLASSE_CONTROLE}
                {...register('codigoLaboratorio')}
              />
            </Campo>

            <Campo
              id="codigoVertis"
              rotulo="Código do Vertis"
              erro={errors.codigoVertis?.message}
              dica="É por ele que a integração casa o exame."
            >
              <Input id="codigoVertis" className={CLASSE_CONTROLE} {...register('codigoVertis')} />
            </Campo>
          </div>

          <Campo id="descricao" rotulo="Descrição" erro={errors.descricao?.message}>
            <Textarea
              id="descricao"
              className={CLASSE_CONTROLE}
              rows={3}
              {...register('descricao')}
            />
          </Campo>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Criar exame'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
